import { SdkError, SdkValidationError } from './client/errors';
import { KEY_RE } from './constants';

export interface Storage {
  get<T = unknown>(key: string): T | null;
  set(key: string, value: unknown): void;
  delete(key: string): void;
  list(): string[];
}

function validateKey(key: string): void {
  if (!KEY_RE.test(key)) {
    throw new SdkValidationError(`Invalid storage key: ${key}`, 'storage.bad_key', 400);
  }
}

function getPrefix(appId: string, userId: string | null): string {
  return `hook:${appId}:${userId ?? '_anon'}:`;
}

export function createStorage(appId: string, getUserId: () => string | null): Storage {
  // Throttle: warna só 1x por instância de Storage quando localStorage indisponível
  // (modo privado, SecurityError, etc.). Bundle pode chamar set 50× em modo
  // privado — sem isso, virariam 50 console.warn.
  let unavailableWarned = false;

  return {
    get<T = unknown>(key: string): T | null {
      validateKey(key);
      const prefix = getPrefix(appId, getUserId());
      try {
        const raw = localStorage.getItem(prefix + key);
        if (raw === null) return null;
        return JSON.parse(raw) as T;
      } catch (err) {
        if (err instanceof SdkError) throw err;
        // localStorage indisponível ou JSON inválido — degraded
        return null;
      }
    },

    set(key: string, value: unknown): void {
      validateKey(key);
      const prefix = getPrefix(appId, getUserId());
      try {
        localStorage.setItem(prefix + key, JSON.stringify(value));
      } catch (err) {
        if (err instanceof DOMException && /quota/i.test(err.name + err.message)) {
          throw new SdkError('localStorage quota exceeded', 'storage.quota', 0);
        }
        // Modo privado / localStorage indisponível — no-op silent + warn 1x
        if (!unavailableWarned) {
          console.warn('[hook/sdk] localStorage unavailable; storage.set is no-op');
          unavailableWarned = true;
        }
      }
    },

    delete(key: string): void {
      validateKey(key);
      const prefix = getPrefix(appId, getUserId());
      try {
        localStorage.removeItem(prefix + key);
      } catch {
        // no-op
      }
    },

    list(): string[] {
      const prefix = getPrefix(appId, getUserId());
      const keys: string[] = [];
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const fullKey = localStorage.key(i);
          if (fullKey?.startsWith(prefix)) {
            keys.push(fullKey.slice(prefix.length));
          }
        }
      } catch {
        // no-op
      }
      return keys;
    },
  };
}
